import { SelectField, TextField, ActionButtons } from '@dispatch/web/ui';
import { Card, CardContent, Grid } from '@mui/material';
import { FieldError, useFieldArray } from 'react-hook-form';
import { FieldRow } from '../styles';
import { useState } from 'react';
import { RenderQualifier } from './RenderQualifiers';
import {
  TaskFormComponentProps,
  MultiSelectOption,
} from '@dispatch/shared/types';

export const RoutingRulesField = ({
  control,
  errors,
  rulesState = [],
  setValue,
  name,
  perilOptions,
  taskGroupOptions,
}: TaskFormComponentProps & {
  perilOptions: MultiSelectOption[];
  taskGroupOptions: MultiSelectOption[];
}) => {
  const [ifField, setIfField] = useState([]);
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });
  const rulesCurrent = [...rulesState];
  const renderError = (error: FieldError) => {
    if (!error) return null;
    return error?.message;
  };

  return (
    <div
      style={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}
      data-testid="routing-rules"
    >
      <div style={{ width: '100%' }}>
        <Card sx={{ width: '100%', marginBottom: '0.625rem' }}>
          <CardContent>
            {fields.map((_, index) => {
              const rule = rulesCurrent[index];
              return (
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    margin: '0.625rem 0',
                  }}
                  key={index}
                >
                  <Grid
                    container
                    spacing={3}
                    sx={{
                      '&.MuiGrid-root': {
                        alignItems:
                          errors['rules'] && errors['rules'][index]?.meta
                            ? 'baseline'
                            : 'end',
                      },
                    }}
                    marginBottom={1}
                  >
                    <Grid size={3}>
                      <SelectField
                        onValueChange={(value: any) => {
                          setValue && setValue(`${name}[${index}].meta`, []);
                          const updateIf: any = [...ifField];
                          updateIf[index] = value?.value;
                          setIfField(updateIf);
                        }}
                        id={`${name}[${index}].ifThis`}
                        name={`${name}[${index}].ifThis`}
                        label="If This"
                        control={control}
                        errors={errors}
                        error={
                          errors[name] &&
                          renderError(errors[name][index]?.ifThis)
                        }
                        options={[
                          {
                            value: '$msg.claim.lossLocation.state',
                            title: 'State',
                          },
                          {
                            value: '$msg.claim.lossLocation.region',
                            title: 'Region',
                          },
                          { value: '$msg.claim.peril', title: 'Peril' },
                        ]}
                      />
                    </Grid>
                    <Grid size={3}>
                      <SelectField
                        id={`${name}[${index}].operator`}
                        name={`${name}[${index}].operator`}
                        label="Operator"
                        control={control}
                        errors={errors}
                        error={
                          errors[name]
                            ? errors[name][index]?.operator?.message
                            : null
                        }
                        options={[
                          { value: '@in_list', title: 'Is In' },
                          { value: '@not_in_list', title: 'Not In' },
                          { value: '@equals', title: 'Equals' },
                        ]}
                      />
                    </Grid>
                    <Grid sx={{ alignSelf: 'flex-end' }} size={5}>
                      <RenderQualifier
                        perilOptions={perilOptions}
                        ifStatement={ifField[index] || rule?.ifThis}
                        index={index}
                        errors={errors}
                        control={control}
                      />
                    </Grid>
                    <Grid
                      sx={{
                        paddingLeft: '1px',
                        marginLeft: '-24px',
                        width: 'calc((100% + 24px) / 12)',
                      }}
                    >
                      <ActionButtons
                        index={index}
                        fields={fields}
                        append={() => {
                          if (fields.length < 2) {
                            append({
                              ifThis: '',
                              operator: '',
                              meta: [],
                            });
                          }
                        }}
                        remove={() => remove(index)}
                      />
                    </Grid>
                    <Grid size={2}>
                      {index !== fields.length - 1 ? (
                        <SelectField
                          id={`condition`}
                          name={`condition`}
                          label="Condition"
                          control={control}
                          errors={errors}
                          error={
                            errors['condition'] &&
                            renderError(errors['condition'])
                          }
                          options={[
                            { value: '@and', title: 'and' },
                            { value: '@or', title: 'or' },
                          ]}
                        />
                      ) : null}
                    </Grid>
                  </Grid>
                </div>
              );
            })}
            <Grid
              container
              spacing={2}
              sx={{
                '& .MuiTextField-root': { margin: 0, width: '100%' },
                '&.MuiGrid-root': { alignItems: 'baseline' },
              }}
            >
              <Grid size={8}>
                <FieldRow>
                  <TextField
                    inputProps={{ maxLength: 200 }}
                    // required
                    id="ruleDescription"
                    name="ruleDescription"
                    label="Rule Description"
                    control={control}
                    errors={errors}
                    sx={{ flex: 1 }}
                    multiline
                  />
                </FieldRow>
              </Grid>
              <Grid sx={{ alignSelf: 'flex-end' }} size={4}>
                <SelectField
                  id={`targetTaskGroupId`}
                  name={`targetTaskGroupId`}
                  label="Move to Task Group"
                  control={control}
                  errors={errors}
                  error={errors['group'] && renderError(errors['group'])}
                  options={taskGroupOptions}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
